import { useEffect, useRef, useState } from "react";

import { useApi } from "../../shared/api/api-context.js";
import { requireData } from "../../shared/api/api-error.js";
import { Button } from "../../shared/ui/button.js";
import type { BacktestView } from "./backtest-models.js";
import { exportBacktestEvidence } from "./evidence-export.js";

export function EvidenceDownload({ run }: { readonly run: BacktestView }) {
  const api = useApi();
  const controller = useRef<AbortController | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState("");
  useEffect(() => () => controller.current?.abort(), []);
  const download = async () => {
    const active = new AbortController();
    controller.current = active;
    setError("");
    setProgress(0);
    try {
      const blob = await exportBacktestEvidence(
        run,
        async (cursor) =>
          requireData(
            await api.GET("/backtests/{backtestId}/chart", {
              params: {
                path: { backtestId: run.id },
                query: { mode: "artifact", ...(cursor === undefined ? {} : { cursor }) },
              },
              signal: active.signal,
            }),
          ),
        active.signal,
        setProgress,
      );
      active.signal.throwIfAborted();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${run.request.symbol}-${run.id}-evidence.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (failure) {
      if (!active.signal.aborted)
        setError(
          failure instanceof Error
            ? failure.message
            : "Evidence download failed. No file was saved.",
        );
    } finally {
      if (controller.current === active) {
        controller.current = null;
        setProgress(null);
      }
    }
  };
  return (
    <div className="section-stack">
      <div className="button-row">
        <Button disabled={progress !== null} tone="quiet" onClick={() => void download()}>
          {progress === null
            ? "Download complete evidence JSON"
            : `Downloading evidence · ${progress} pages`}
        </Button>
        {progress === null ? null : (
          <Button tone="quiet" onClick={() => controller.current?.abort()}>
            Cancel download
          </Button>
        )}
      </div>
      {error === "" ? null : <p role="alert">{error}</p>}
      <p className="field-hint">
        Downloads all authenticated evidence pages, including hold decisions and equity points. No
        public storage link is needed. Legacy reconstructed candles are explicitly labelled in the
        file.
      </p>
    </div>
  );
}
